// InfrastructureTab.js
import React, { useState, useEffect } from 'react';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { baseURL, fetchAgentsData } from './DataFlow';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';

const InfrastructureTab = () => {
    const isAdmin = localStorage.getItem('isAppPowerUser') === 'true';

    const [agentsData, setAgentsData] = useState([]);
    const [agentsFirst, setAgentsFirst] = useState(0);
    const [agentsRows, setAgentsRows] = useState(5);
    const [selectedNodeKey, setSelectedNodeKey] = useState(null);

    // State variables for modal editing
    const [selectedNode, setSelectedNode] = useState(null);
    const [isDialogVisible, setIsDialogVisible] = useState(false);

    // Add this new state variable with the other state declarations
    const [expandedKeys, setExpandedKeys] = useState({});

    useEffect(() => {
        fetchAgentsData(setAgentsDataWithKeys);
    }, []);

    useEffect(() => {
        // When agentsData changes, create an object with all keys set to true
        if (agentsData.length > 0) {
            const allKeys = {};
            agentsData.forEach((node) => {
                allKeys[node.key] = true;
                if (node.children) {
                    node.children.forEach((child) => {
                        allKeys[child.key] = true;
                    });
                }
            });
            setExpandedKeys(allKeys);
        }
    }, [agentsData]);

    const setAgentsDataWithKeys = (data) => {
        setAgentsData(data);
    };

    const onAgentsPageChange = (event) => {
        setAgentsFirst(event.first);
        setAgentsRows(event.rows);
    };

    const actionTemplate = (node) => {
        if (!isAdmin) return null;

        return (
            <div>
                <Button
                    icon="pi pi-pencil"
                    onClick={() => onEditNode(node)}
                    className="p-button-text p-button-sm"
                    tooltip="Edit"
                    tooltipOptions={{ position: 'top' }}
                />
                <Button
                    icon="pi pi-trash"
                    onClick={() => onDeleteNode(node)}
                    className="p-button-text p-button-sm p-ml-2"
                    tooltip="Delete"
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    const onEditNode = (node) => {
        setSelectedNode({ ...node }); // Create a copy to avoid direct mutations
        setIsDialogVisible(true);
    };

    const onSaveNode = async () => {
        setIsDialogVisible(false);
        const node = selectedNode;
        
        const id = node.data._id;
        const url = baseURL + 
            (node.node_type === 'agent' ? '/api/agents/update/' : '/api/devices/update/') + 
            id;

        const dataClone = { ...node.data };
        delete dataClone.name;

        // Make sure numeric fields are properly formatted for devices
        if (node.node_type === 'device') {
            dataClone.screenWidth = dataClone.screenWidth ? Number(dataClone.screenWidth) : null;
            dataClone.screenHeight = dataClone.screenHeight ? Number(dataClone.screenHeight) : null;
            dataClone.ram = dataClone.ram ? Number(dataClone.ram) : null;
        }

        // Comments field will be included in dataClone automatically

        const authToken = localStorage.getItem('apiToken');
        try {
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    api_token: authToken,
                },
                body: JSON.stringify(dataClone),
            });

            if (response.ok) {
                fetchAgentsData(setAgentsDataWithKeys);
            } else {
                alert('Failed to save changes. Please try again.');
                console.error('Error saving node:', response.statusText);
            }
        } catch (error) {
            console.error('Error saving node:', error);
            alert('An error occurred while saving the node.');
        }
    };

    const onDialogValueChange = (field, value) => {
        setSelectedNode((prevNode) => ({
            ...prevNode,
            data: {
                ...prevNode.data,
                [field]: value,
                // Update 'name' field for table display
                ...(field === 'agentName' || field === 'model' ? { name: value } : {}),
            },
        }));
    };

    const onDeleteNode = (node) => {
        let message;

        if (node.node_type === 'device') {
            message = `Delete device [${node.data.model}]?`;
        } else if (node.node_type === 'agent') {
            message = `Deleting [${node.data.agentName}] will orphan any attached devices! Continue?`;
        }

        confirmDialog({
            message: message,
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteNodeFromServer(node),
            reject: () => {
                // Do nothing
            },
        });
    };

    const deleteNodeFromServer = async (node) => {
        const id = node.data._id;
        const url =
            baseURL +
            (node.node_type === 'agent' ? '/api/agents/' : '/api/devices/') +
            id;

        const authToken = localStorage.getItem('apiToken');

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    api_token: authToken,
                },
            });

            if (response.ok) {
                // Refresh data
                fetchAgentsData(setAgentsDataWithKeys);
            } else {
                // Optionally, display an error message to the user
                alert('Failed to delete node. Please try again.');
                console.error('Error deleting node:', response.statusText);
            }
        } catch (error) {
            console.error('Error deleting node:', error);
            alert('An error occurred while deleting the node.');
        }
    };

    const onAddAgent = async () => {
        const newAgentData = {
            name: 'New Agent',
            agentName: 'New Agent',
            ipAddress: '',
            platform: '',
            platformVersion: '',
            status: '',
            lastUsed: '',
            enabled: false,
        };

        try {
            const url = baseURL + '/api/agents/';
            const authToken = localStorage.getItem('apiToken');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    api_token: authToken,
                },
                body: JSON.stringify(newAgentData),
            });

            if (response.ok) {
                fetchAgentsData(setAgentsDataWithKeys);
            } else {
                alert('Failed to add agent. Please try again.');
                console.error('Error adding agent:', response.statusText);
            }
        } catch (error) {
            console.error('Error adding agent:', error);
            alert('An error occurred while adding the agent.');
        }
    };

    const onAddDevice = async () => {
        if (!selectedNodeKey) {
            alert('Please select an agent to add a device to.');
            return;
        }

        const findNodeByKey = (nodes, key) => {
            for (let node of nodes) {
                if (node.key === key) {
                    return node;
                } else if (node.children) {
                    const found = findNodeByKey(node.children, key);
                    if (found) return found;
                }
            }
            return null;
        };

        const selectedNode = findNodeByKey(agentsData, selectedNodeKey);

        if (!selectedNode || selectedNode.data.node_type !== 'agent') {
            alert('Please select an agent to add a device to.');
            return;
        }

        const newDeviceData = {
            name: 'New Device',
            model: 'New Device',
            ipAddress: '',
            platform: '',
            platformVersion: '',
            status: '',
            udid: '',
            lastUsed: '',
            enabled: false,
            agentId: selectedNode.data._id,
        };

        try {
            const url = baseURL + '/api/devices/';
            const authToken = localStorage.getItem('apiToken');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    api_token: authToken,
                },
                body: JSON.stringify(newDeviceData),
            });

            if (response.ok) {
                fetchAgentsData(setAgentsDataWithKeys);
            } else {
                alert('Failed to add device. Please try again.');
                console.error('Error adding device:', response.statusText);
            }
        } catch (error) {
            console.error('Error adding device:', error);
            alert('An error occurred while adding the device.');
        }
    };

    const rowClassName = (rowData) => {
        if (rowData.data.node_type === 'agent') {
            return 'agent-row';
        }

        return !rowData.data.enabled ? 'disabled-row' : '';
    };

    const nameBodyTemplate = (node) => {
        return <span>{node.data.name}</span>;
    };

    const ipAddressBodyTemplate = (node) => {
        return node.data.ipAddress;
    };

    const platformBodyTemplate = (node) => {
        return node.data.platform;
    };

    const platformVersionBodyTemplate = (node) => {
        return node.data.platformVersion;
    };

    const statusBodyTemplate = (node) => {
        return node.data.status;
    };

    const typeBodyTemplate = (node) => {
        if (node.node_type === 'agent') {
            return null;
        }
        return node.data.type;
    };

    const enabledBodyTemplate = (node) => {
        return node.data.enabled ? 'Yes' : 'No';
    };

    useEffect(() => {
        // Cleanup function to ensure scroll is restored when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []); // Empty dependency array means this runs on mount and cleanup on unmount

    const preventBodyScroll = () => {
        // Only prevent scroll if dialog is actually visible
        // if (isDialogVisible) {
        //     document.body.style.overflow = 'hidden';
        // }
    };

    const restoreBodyScroll = () => {
        // Only restore scroll if we're actually hidden
        //if (!isDialogVisible) {
            document.body.style.overflow = 'auto';
        //}
    };

    return (
        <div>
            {isAdmin && (
                <div className="p-mb-2 button-container" style={{ width: '20%', paddingBottom:'10px' }}>
                    <Button
                        label="Agent"
                        icon="pi pi-plus"
                        onClick={onAddAgent}
                        className="p-button-success p-button-sm"
                        tooltip="Add Agent"
                        tooltipOptions={{ position: 'top' }}
                    />
                    <Button
                        label="Device"
                        icon="pi pi-plus"
                        onClick={onAddDevice}
                        className="p-button-info p-button-sm"
                        tooltip="Add Device"
                        tooltipOptions={{ position: 'top' }}
                    />
                </div>
            )}
            <TreeTable
                value={agentsData}
                expandedKeys={expandedKeys}
                onToggle={e => setExpandedKeys(e.value)}
                first={agentsFirst}
                paginator
                showGridlines
                rows={agentsRows}
                onPage={onAgentsPageChange}
                className="dark-table custom-row-spacing"
                selectionMode="single"
                selectionKeys={selectedNodeKey}
                onSelectionChange={(e) => setSelectedNodeKey(e.value)}
                rowClassName={rowClassName}
                scrollable
                scrollHeight="flex"
            >
                <Column
                    field="name"
                    header="Name"
                    expander
                    body={nameBodyTemplate}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
                <Column
                    field="ipAddress"
                    header="IP Address"
                    body={ipAddressBodyTemplate}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
                <Column
                    field="platform"
                    header="Platform"
                    body={platformBodyTemplate}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
                <Column
                    field="platformVersion"
                    header="OS"
                    body={platformVersionBodyTemplate}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
                <Column
                    field="status"
                    header="Status"
                    body={statusBodyTemplate}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
                <Column
                    field="type"
                    header="Type"
                    body={typeBodyTemplate}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
                <Column
                    field="enabled"
                    header="Enabled"
                    body={enabledBodyTemplate}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
                <Column
                    field="comments"
                    header="Comments"
                    body={(node) => node.data.comments || ''}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
                <Column
                    header="Actions"
                    body={actionTemplate}
                    headerClassName="column-spacing"
                    bodyClassName="column-spacing"
                />
            </TreeTable>
            <ConfirmDialog />

            {/* Modal Dialog for Editing */}
            <Dialog
                header={`Edit ${selectedNode?.node_type === 'agent' ? 'Agent' : 'Device'}`}
                visible={isDialogVisible}
                style={{ width: '60vw' }}
                modal
                onHide={() => {
                    setIsDialogVisible(false);
                    restoreBodyScroll();
                }}
                onShow={preventBodyScroll}
                className="edit-dialog"
            >
                {selectedNode && (
                    <div className="p-fluid edit-dialog-container">
                        {/* Basic Info Section */}
                        <div className="section-header">Basic Information</div>
                        <div className="p-grid p-formgrid">
                            <div className="p-col-12 p-md-4">
                                <div className="p-field">
                                    <label htmlFor="name">Name</label>
                                    <InputText
                                        id="name"
                                        value={selectedNode.node_type === 'agent' ? selectedNode.data.agentName : selectedNode.data.model}
                                        onChange={(e) => onDialogValueChange(
                                            selectedNode.node_type === 'agent' ? 'agentName' : 'model',
                                            e.target.value
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="p-col-12 p-md-4">
                                <div className="p-field">
                                    <label htmlFor="ipAddress">IP Address</label>
                                    <InputText
                                        id="ipAddress"
                                        value={selectedNode.data.ipAddress}
                                        onChange={(e) => onDialogValueChange('ipAddress', e.target.value)}
                                        keyfilter={/[\d.]/}
                                    />
                                </div>
                            </div>
                            <div className="p-col-12 p-md-4">
                                <div className="p-field">
                                    <label htmlFor="platform">Platform</label>
                                    <Dropdown
                                        id="platform"
                                        value={selectedNode.data.platform}
                                        options={[
                                            { label: 'Android', value: 'android' },
                                            { label: 'iOS', value: 'ios' },
                                        ]}
                                        onChange={(e) => onDialogValueChange('platform', e.value)}
                                    />
                                </div>
                            </div>
                            <div className="p-col-12 p-md-4">
                                <div className="p-field">
                                    <label htmlFor="platformVersion">OS Version</label>
                                    <InputText
                                        id="platformVersion"
                                        value={selectedNode.data.platformVersion}
                                        onChange={(e) => onDialogValueChange('platformVersion', e.target.value)}
                                    />
                                </div>
                            </div>
                            {selectedNode.node_type === 'agent' && (
                                <div className="p-col-12 p-md-4">
                                    <div className="p-field">
                                        <label htmlFor="status">Status</label>
                                        <Dropdown
                                            id="status"
                                            value={selectedNode.data.status}
                                            options={[
                                                { label: 'Available', value: 'Available' },
                                                { label: 'Busy', value: 'Busy' },
                                                { label: 'NotWorking', value: 'NotWorking' },
                                            ]}
                                            onChange={(e) => onDialogValueChange('status', e.value)}
                                        />
                                    </div>
                                </div>
                            )}
                            {selectedNode.node_type === 'device' && (
                                <div className="p-col-12 p-md-4">
                                    <div className="p-field">
                                        <label htmlFor="type">Type</label>
                                        <Dropdown
                                            id="type"
                                            value={selectedNode.data.type}
                                            options={[
                                                { label: 'On-premise', value: 'On-premise' },
                                                { label: 'Cloud', value: 'Cloud' },
                                            ]}
                                            onChange={(e) => onDialogValueChange('type', e.value)}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="p-col-12 p-md-4">
                                <div className="p-field-checkbox checkbox-container">
                                    <Checkbox
                                        inputId="enabled"
                                        checked={selectedNode.data.enabled || false}
                                        onChange={(e) => onDialogValueChange('enabled', e.checked)}
                                    />
                                    <label htmlFor="enabled" style={{paddingLeft: '5px'}}>Enabled</label>
                                </div>
                            </div>
                        </div>

                        {/* Device Specific Section */}
                        {selectedNode.node_type === 'device' && (
                            <>
                                <div className="section-header">Device Specific Details</div>
                                <div className="p-grid p-formgrid">
                                    <div className="p-col-12 p-md-6">
                                        <div className="p-field">
                                            <label htmlFor="udid">UDID</label>
                                            <InputText
                                                id="udid"
                                                value={selectedNode.data.udid}
                                                onChange={(e) => onDialogValueChange('udid', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-6">
                                        <div className="p-field">
                                            <label htmlFor="screenWidth">Screen Width (pixels)</label>
                                            <InputNumber
                                                id="screenWidth"
                                                value={selectedNode.data.screenWidth}
                                                onValueChange={(e) => onDialogValueChange('screenWidth', e.value)}
                                                mode="decimal"
                                                min={0}
                                                useGrouping={false}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-6">
                                        <div className="p-field">
                                            <label htmlFor="screenHeight">Screen Height (pixels)</label>
                                            <InputNumber
                                                id="screenHeight"
                                                value={selectedNode.data.screenHeight}
                                                onValueChange={(e) => onDialogValueChange('screenHeight', e.value)}
                                                mode="decimal"
                                                min={0}
                                                useGrouping={false}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-6">
                                        <div className="p-field">
                                            <label htmlFor="ram">RAM (GB)</label>
                                            <InputNumber
                                                id="ram"
                                                value={selectedNode.data.ram}
                                                onValueChange={(e) => onDialogValueChange('ram', e.value)}
                                                mode="decimal"
                                                minFractionDigits={1}
                                                maxFractionDigits={1}
                                                min={0}
                                                useGrouping={false}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-6">
                                        <div className="p-field">
                                            <label htmlFor="hsUdid">HS UDID</label>
                                            <InputText
                                                id="hsUdid"
                                                value={selectedNode.data.hsUdid}
                                                onChange={(e) => onDialogValueChange('hsUdid', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-6">
                                        <div className="p-field">
                                            <label htmlFor="hsServerUrl">HS Server URL</label>
                                            <InputText
                                                id="hsServerUrl"
                                                value={selectedNode.data.hsServerUrl}
                                                onChange={(e) => onDialogValueChange('hsServerUrl', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-6">
                                        <div className="p-field">
                                            <label htmlFor="deviceAddress">Device Address</label>
                                            <InputText
                                                id="deviceAddress"
                                                value={selectedNode.data.deviceAddress}
                                                onChange={(e) => onDialogValueChange('deviceAddress', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-col-12">
                                        <div className="p-field-checkbox checkbox-container">
                                            <Checkbox
                                                inputId="useRemoteHeadSpinServer"
                                                checked={selectedNode.data.useRemoteHeadSpinServer || false}
                                                onChange={(e) => onDialogValueChange('useRemoteHeadSpinServer', e.checked)}
                                            />
                                            <label htmlFor="useRemoteHeadSpinServer" style={{paddingLeft: '5px'}}>Use Remote HS</label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Add a new Comments section at the bottom of both agent and device forms */}
                        <div className="section-header">Comments</div>
                        <div className="p-grid p-formgrid">
                            <div className="p-col-12">
                                <div className="p-field">
                                    <label htmlFor="comments">Comments</label>
                                    <InputText
                                        id="comments"
                                        value={selectedNode.data.comments}
                                        onChange={(e) => onDialogValueChange('comments', e.target.value)}
                                        className="p-inputtext-lg"
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="dialog-footer">
                            <Button
                                label="Save"
                                icon="pi pi-check"
                                onClick={onSaveNode}
                                className="p-button-sm p-mr-2"
                            />
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-secondary p-button-sm"
                                onClick={() => setIsDialogVisible(false)}
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );

};

export default InfrastructureTab;
